import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Maximize2,
  Minimize2,
  Settings,
  User,
  Users,
  X,
} from "lucide-react";

import { cn } from "lib/utils";
import { DatePicker } from "js/components/ui/date-picker";

import {
  formatCurrency,
  ORDER_STATUS_LABELS,
  TRANSACTION_SOURCE_LABELS,
  TRANSACTION_TYPE_LABELS,
} from "js/components/Quotas/QuotaConstants";

import type {
  OrderStatus,
  QuotaOrder,
  QuotaTransaction,
  TransactionType,
} from "../types";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ActivityFeedProps {
  orders: QuotaOrder[];
  transactions: QuotaTransaction[];
  isLoadingOrders: boolean;
  isLoadingTransactions: boolean;
  isErrorOrders?: boolean;
  isErrorTransactions?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  /** Page size used by the API queries — used to hint if more results exist */
  pageSize?: number;
  /** When set, only show transactions performed by this email (orders are excluded) */
  filterByEmail?: string | null;
  onExport?: () => void;
  isExporting?: boolean;
  /** Called when the user dismisses the email filter chip */
  onClearFilter?: () => void;
  /** Called when the user selects a quote_type filter */
  onQuoteTypeFilterChange?: (quoteType: "PF" | "PM" | null) => void;
  /** Current quote_type filter */
  quoteTypeFilter?: "PF" | "PM" | null;
  onDateFilterChange?: (dateFrom: string | undefined, dateTo: string | undefined) => void;
  dateFrom?: string;
  dateTo?: string;
  /** Current active tab — source of truth lives in the parent */
  activeTab: FilterTab;
  onTabChange?: (tab: FilterTab) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

type FeedItem =
  | { type: "order"; data: QuotaOrder; date: Date }
  | { type: "transaction"; data: QuotaTransaction; date: Date };

type FilterTab = "all" | "purchases" | "consumption" | "distribution";

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: "all", label: "Todos" },
  { key: "purchases", label: "Compras" },
  { key: "consumption", label: "Consumo" },
  { key: "distribution", label: "Distribución" },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hace ${diffHr}h`;
  return new Date(dateStr).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDateLabel(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (d.getTime() === today.getTime()) return "Hoy";
  if (d.getTime() === yesterday.getTime()) return "Ayer";
  return date
    .toLocaleDateString("es-MX", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(".", "");
}

function mergeAndSort(
  orders: QuotaOrder[],
  transactions: QuotaTransaction[],
): FeedItem[] {
  // Completed orders always have a PURCHASE transaction in the feed,
  // so showing both is redundant.  Filter by status instead of
  // cross-referencing the current transactions page (which breaks
  // when the two records land on different pages).
  const items: FeedItem[] = [
    ...orders
      .filter((o) => o.status !== "COMPLETED")
      .map(
        (o) =>
          ({ type: "order" as const, data: o, date: new Date(o.created_at) }),
      ),
    ...transactions.map(
      (t) =>
        ({
          type: "transaction" as const,
          data: t,
          date: new Date(t.created_at),
        }),
    ),
  ];

  items.sort((a, b) => b.date.getTime() - a.date.getTime());
  return items;
}

function groupByDate(
  items: FeedItem[],
): { label: string; items: FeedItem[] }[] {
  const groups: { label: string; items: FeedItem[] }[] = [];
  for (const item of items) {
    const label = getDateLabel(item.date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}

function filterItems(items: FeedItem[], filter: FilterTab): FeedItem[] {
  if (filter === "all") return items;

  if (filter === "purchases") {
    return items.filter(
      (item) =>
        item.type === "order" ||
        (item.type === "transaction" &&
          item.data.transaction_type === "PURCHASE"),
    );
  }

  if (filter === "consumption") {
    return items.filter(
      (item) =>
        item.type === "transaction" &&
        item.data.transaction_type === "CONSUMPTION",
    );
  }

  if (filter === "distribution") {
    return items.filter(
      (item) =>
        item.type === "transaction" &&
        item.data.transaction_type === "DISTRIBUTION",
    );
  }

  return items;
}

/* ------------------------------------------------------------------ */
/*  Status / icon maps                                                 */
/* ------------------------------------------------------------------ */

const ORDER_STATUS_TEXT_COLORS: Record<OrderStatus, string> = {
  COMPLETED: "text-emerald-600",
  PAYMENT_IN_PROGRESS: "text-amber-600",
  PENDING: "text-amber-600",
  FAILED: "text-rose-600",
  CANCELLED: "text-muted-foreground",
};

const ORDER_STATUS_BG: Record<OrderStatus, string> = {
  COMPLETED: "bg-emerald-50",
  PAYMENT_IN_PROGRESS: "bg-amber-50",
  PENDING: "bg-amber-50",
  FAILED: "bg-rose-50",
  CANCELLED: "bg-muted",
};

const TRANSACTION_ICON_MAP: Record<TransactionType, React.ElementType> = {
  PURCHASE: ArrowUpRight,
  CONSUMPTION: ArrowDownRight,
  ADMIN_ADJUSTMENT: Settings,
  DISTRIBUTION: Users,
};

const TRANSACTION_ICON_COLORS: Record<TransactionType, string> = {
  PURCHASE: "text-emerald-600",
  CONSUMPTION: "text-rose-600",
  ADMIN_ADJUSTMENT: "text-amber-600",
  DISTRIBUTION: "text-blue-600",
};


/* ------------------------------------------------------------------ */
/*  Amount size helper                                                 */
/* ------------------------------------------------------------------ */


/* ------------------------------------------------------------------ */
/*  Row renderers                                                      */
/* ------------------------------------------------------------------ */

function OrderRow({ order }: { order: QuotaOrder }) {
  const navigate = useNavigate();
  const isCancelledOrFailed =
    order.status === "CANCELLED" || order.status === "FAILED";
  const amount = order.total_amount;

  const iconColor =
    order.quote_type === "PM"
      ? "text-violet-600"
      : "text-indigo-600";

  const OrderIcon = order.quote_type === "PM" ? Building2 : User;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() =>
        navigate(`/organizations/quotas/orders/${order.id}`)
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/organizations/quotas/orders/${order.id}`);
        }
      }}
      className={cn(
        "flex items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-muted/30 transition-colors cursor-pointer",
        isCancelledOrFailed && "opacity-50",
      )}
    >
      <OrderIcon className={cn("size-5 shrink-0", iconColor)} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-foreground leading-tight">
          Compra {order.quantity.toLocaleString("es-MX")}
        </p>
        <span
          className={cn(
            "inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium mt-0.5",
            ORDER_STATUS_TEXT_COLORS[order.status],
            ORDER_STATUS_BG[order.status],
          )}
        >
          {ORDER_STATUS_LABELS[order.status]}
        </span>
      </div>

      <div className="hidden sm:block w-28 shrink-0">
        <span
          className={cn(
            "inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
            order.quote_type === "PM"
              ? "bg-violet-50 text-violet-700"
              : "bg-indigo-50 text-indigo-700",
          )}
        >
          {order.quote_type === "PM" ? "Persona Moral" : "Persona Física"}
        </span>
      </div>

      <div className="w-20 sm:w-24 shrink-0 text-right">
        <span
          className={cn(
            "text-[13px] font-semibold tabular-nums text-foreground leading-tight",
            isCancelledOrFailed && "line-through decoration-muted-foreground/30",
          )}
        >
          {formatCurrency(amount)}
        </span>
        <p className="text-[10px] text-muted-foreground/70 tabular-nums leading-tight mt-0.5">
          {relativeTime(order.created_at)}
        </p>
      </div>

      <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/50" />
    </div>
  );
}

function TransactionRow({ tx }: { tx: QuotaTransaction }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const isPositive = tx.quantity > 0;
  const Icon = TRANSACTION_ICON_MAP[tx.transaction_type] ?? ArrowDownRight;
  const iconColor =
    TRANSACTION_ICON_COLORS[tx.transaction_type] ?? "text-slate-600";

  const emailUser = tx.performed_by_email
    ? tx.performed_by_email.split("@")[0]
    : null;

  return (
    <div
      className="rounded-lg px-3 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
      onClick={() => setExpanded(!expanded)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(!expanded); } }}
    >
      <div className="flex items-center gap-2.5">
        <Icon className={cn("size-5 shrink-0", iconColor)} />

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "truncate text-[13px] font-medium leading-tight",
              isPositive ? "text-emerald-600" : "text-rose-600",
            )}
          >
            {isPositive ? "+" : ""}
            {tx.quantity.toLocaleString("es-MX")}
          </p>
          <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
            {TRANSACTION_TYPE_LABELS[tx.transaction_type]}
          </p>
          {tx.notes && (
            <p className="truncate text-[11px] italic text-muted-foreground/70 leading-tight mt-0.5">
              {tx.notes}
            </p>
          )}
          {emailUser && (
            <p className="truncate text-[10px] text-muted-foreground/60 leading-tight mt-0.5">
              por {emailUser}
            </p>
          )}
        </div>

        <div className="hidden sm:block w-28 shrink-0">
          <span
            className={cn(
              "inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
              tx.quote_type === "PM"
                ? "bg-violet-50 text-violet-700"
                : "bg-indigo-50 text-indigo-700",
            )}
          >
            {tx.quote_type === "PM" ? "Persona Moral" : "Persona Física"}
          </span>
        </div>

        <div className="w-16 sm:w-24 shrink-0 text-right">
          <span className="text-[10px] text-muted-foreground/70 tabular-nums">
            {relativeTime(tx.created_at)}
          </span>
        </div>

        <ChevronDown className={cn("size-3.5 shrink-0 text-muted-foreground/40 transition-transform", expanded && "rotate-180")} />
      </div>
      {expanded && (
        <div className="mt-2 ml-[1.875rem] grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg bg-muted/20 px-3 py-2 text-[11px]">
          {tx.balance_before != null && tx.balance_after != null && (
            <>
              <span className="text-muted-foreground">Saldo antes</span>
              <span className="font-medium tabular-nums text-foreground">{tx.balance_before.toLocaleString("es-MX")}</span>
              <span className="text-muted-foreground">Saldo después</span>
              <span className="font-medium tabular-nums text-foreground">{tx.balance_after.toLocaleString("es-MX")}</span>
            </>
          )}
          {tx.source && (
            <>
              <span className="text-muted-foreground">Origen</span>
              <span className="font-medium text-foreground">{TRANSACTION_SOURCE_LABELS[tx.source] ?? tx.source}</span>
            </>
          )}
          {tx.reference_id && (
            <>
              <span className="text-muted-foreground">Referencia</span>
              <span className="font-mono text-foreground truncate">{tx.reference_id}</span>
            </>
          )}
          {tx.performed_by_email && (
            <>
              <span className="text-muted-foreground">Realizado por</span>
              <span className="font-medium text-foreground">{tx.performed_by_email}</span>
            </>
          )}
          {tx.transaction_type === "PURCHASE" && tx.reference_id && (
            <button
              type="button"
              className="col-span-2 mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/organizations/quotas/orders/${tx.reference_id}`);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                }
              }}
            >
              Ver detalle de orden
              <ChevronRight className="size-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */

function FeedSkeleton() {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5">
      <div className="mb-3 h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="mb-3 flex gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-6 w-14 animate-pulse rounded-full bg-muted"
          />
        ))}
      </div>
      <div className="space-y-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2.5 px-3 py-2">
            <div className="size-7 shrink-0 animate-pulse rounded-lg bg-muted" />
            <div className="flex-1 space-y-1">
              <div className="h-3 w-36 animate-pulse rounded bg-muted" />
              <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-3 w-14 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Empty state                                                        */
/* ------------------------------------------------------------------ */

const EMPTY_STATE_MESSAGES: Record<FilterTab, { title: string; subtitle: string }> = {
  all: {
    title: "Sin actividad reciente",
    subtitle: "Las compras y movimientos aparecerán aquí",
  },
  purchases: {
    title: "No hay compras registradas",
    subtitle: "Las compras aparecerán aquí cuando se realicen",
  },
  consumption: {
    title: "No hay consumos registrados",
    subtitle: "Los consumos aparecerán aquí cuando se realicen",
  },
  distribution: {
    title: "No hay distribuciones registradas",
    subtitle: "Las distribuciones aparecerán aquí cuando se realicen",
  },
};

function EmptyState({ activeFilter }: { activeFilter: FilterTab }) {
  const messages = EMPTY_STATE_MESSAGES[activeFilter];
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 py-10">
      <Clock className="size-6 stroke-[1.5] text-muted-foreground/40" />
      <p className="text-xs text-muted-foreground">{messages.title}</p>
      <p className="text-[11px] text-muted-foreground/50">
        {messages.subtitle}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function ActivityFeed({
  orders,
  transactions,
  isLoadingOrders,
  isLoadingTransactions,
  isErrorOrders,
  isErrorTransactions,
  isExpanded = false,
  onToggleExpand,
  pageSize = 20,
  filterByEmail,
  onExport,
  isExporting,
  onClearFilter,
  onQuoteTypeFilterChange,
  quoteTypeFilter,
  onDateFilterChange,
  dateFrom,
  dateTo,
  activeTab,
  onTabChange,
  currentPage,
  onPageChange,
}: ActivityFeedProps) {
  const isLoading = isLoadingOrders || isLoadingTransactions;
  const hasError = isErrorOrders || isErrorTransactions;

  const allItems = useMemo(
    () => (isLoading ? [] : mergeAndSort(orders, transactions)),
    [orders, transactions, isLoading],
  );

  /* Compute counts for filter tabs */
  const filterCounts = useMemo(() => {
    let purchases = 0;
    let consumption = 0;
    let distribution = 0;

    for (const item of allItems) {
      if (item.type === "order") {
        purchases++;
      } else {
        const tx = item.data as QuotaTransaction;
        if (tx.transaction_type === "PURCHASE") purchases++;
        else if (tx.transaction_type === "CONSUMPTION") consumption++;
        else if (tx.transaction_type === "DISTRIBUTION") distribution++;
      }
    }

    return { purchases, consumption, distribution };
  }, [allItems]);

  /* Apply tab filter first, then email filter */
  const tabFilteredItems = useMemo(
    () => filterItems(allItems, activeTab),
    [allItems, activeTab],
  );

  const filteredItems = useMemo(() => {
    if (!filterByEmail) return tabFilteredItems;
    return tabFilteredItems.filter((item) => {
      // Orders have no performed_by_email, so skip them when filtering by member
      if (item.type === "order") return false;
      return item.data.performed_by_email === filterByEmail;
    });
  }, [tabFilteredItems, filterByEmail]);

  const groups = useMemo(() => groupByDate(filteredItems), [filteredItems]);

  const mayHaveMore =
    orders.length >= pageSize || transactions.length >= pageSize;

  if (isLoading) {
    return <FeedSkeleton />;
  }

  function getTabLabel(tab: { key: FilterTab; label: string }): string {
    if (tab.key === "all") return tab.label;
    const count = filterCounts[tab.key as keyof typeof filterCounts];
    return count > 0 ? `${tab.label} (${count})` : tab.label;
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-all duration-300"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-1">
        <h3 className="text-base font-semibold text-foreground">
          Actividad reciente{" "}
          {allItems.length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
              {allItems.length}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {isExpanded && (
            <div className="flex gap-1">
              {(["PF", "PM"] as const).map((qt) => (
                <button
                  key={qt}
                  type="button"
                  onClick={() => onQuoteTypeFilterChange?.(quoteTypeFilter === qt ? null : qt)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-medium transition-all",
                    quoteTypeFilter === qt
                      ? qt === "PF" ? "bg-indigo-100 text-indigo-700" : "bg-violet-100 text-violet-700"
                      : "bg-muted/40 text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  {qt === "PF" ? "Persona Física" : "Persona Moral"}
                </button>
              ))}
            </div>
          )}
          {isExpanded && onExport && (
            <button
              type="button"
              onClick={onExport}
              disabled={isExporting}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-all hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <Download className={cn("size-3", isExporting && "animate-bounce")} />
              {isExporting ? "Exportando..." : "Exportar CSV"}
            </button>
          )}
          {onToggleExpand && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {isExpanded ? (
                <Minimize2 className="size-3.5" />
              ) : (
                <Maximize2 className="size-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="px-4 sm:px-5 pt-2 pb-1 overflow-x-auto">
        <div className="inline-flex rounded-full bg-muted/40 p-0.5 whitespace-nowrap">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                onTabChange?.(tab.key);
              }}
              className={cn(
                "rounded-full px-2.5 py-1 text-[11px] font-medium transition-all",
                activeTab === tab.key
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground/80",
              )}
            >
              {getTabLabel(tab)}
            </button>
          ))}
        </div>
      </div>

      {/* Email filter chip */}
      {filterByEmail && (
        <div className="px-4 sm:px-5 pt-1.5 pb-0.5">
          <button
            type="button"
            onClick={onClearFilter}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-100"
          >
            Filtrado por: {filterByEmail}
            <X className="size-3" />
          </button>
        </div>
      )}

      {/* Date range filter (expanded only) */}
      {isExpanded && (
        <div className="flex flex-wrap items-center gap-2 px-4 sm:px-5 pt-1.5">
          <DatePicker
            value={dateFrom ? new Date(dateFrom + "T00:00:00") : undefined}
            onChange={(date) => {
              const val = date ? date.toISOString().split("T")[0] : undefined;
              onDateFilterChange?.(val, dateTo);
            }}
            placeholder="Desde"
            maxDate={dateTo ? new Date(dateTo + "T00:00:00") : new Date()}
            fromYear={2024}
            toYear={new Date().getFullYear()}
            defaultMonth={new Date()}
            className="h-7 w-[130px] text-[11px] border-border/60 bg-muted/30"
          />
          <span className="text-[11px] text-muted-foreground/50">&mdash;</span>
          <DatePicker
            value={dateTo ? new Date(dateTo + "T00:00:00") : undefined}
            onChange={(date) => {
              const val = date ? date.toISOString().split("T")[0] : undefined;
              onDateFilterChange?.(dateFrom, val);
            }}
            placeholder="Hasta"
            minDate={dateFrom ? new Date(dateFrom + "T00:00:00") : undefined}
            maxDate={new Date()}
            fromYear={2024}
            toYear={new Date().getFullYear()}
            defaultMonth={new Date()}
            className="h-7 w-[130px] text-[11px] border-border/60 bg-muted/30"
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => onDateFilterChange?.(undefined, undefined)}
              className="flex items-center gap-1 rounded-full bg-muted/50 px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              Limpiar
              <X className="size-3" />
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {hasError ? (
        <div className="flex flex-col items-center justify-center gap-1.5 py-10 px-4">
          <p className="text-xs font-medium text-destructive">
            Error al cargar la actividad. Intenta de nuevo.
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState activeFilter={activeTab} />
      ) : (
        <div className="relative flex-1 min-h-0">
          <div className="h-full overflow-y-auto px-2 pb-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-border/50">
            {groups.map((group, groupIdx) => (
              <div key={group.label}>
                <div className={cn("flex items-center gap-2 px-3", groupIdx === 0 ? "mt-2 mb-1.5" : "mt-4 mb-1.5")}>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/50">{group.label}</span>
                  <div className="h-px flex-1 bg-border/40" />
                </div>
                {group.items.map((item) =>
                  item.type === "order" ? (
                    <OrderRow
                      key={`order-${item.data.id}`}
                      order={item.data}
                    />
                  ) : (
                    <TransactionRow
                      key={`tx-${item.data.id}`}
                      tx={item.data}
                    />
                  ),
                )}
              </div>
            ))}
            {isExpanded && onPageChange && (
              <div className="flex items-center justify-between px-3 py-2 border-t border-border/30">
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  Página {currentPage ?? 1}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={!currentPage || currentPage <= 1}
                    onClick={() => onPageChange((currentPage ?? 1) - 1)}
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 transition-colors"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={!mayHaveMore}
                    onClick={() => onPageChange((currentPage ?? 1) + 1)}
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
            {!isExpanded && mayHaveMore && onToggleExpand && (
              <button
                type="button"
                onClick={onToggleExpand}
                className="text-[11px] text-indigo-600 font-medium hover:underline py-3 w-full text-center"
              >
                Ver historial completo
              </button>
            )}
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card to-transparent" />
        </div>
      )}
    </div>
  );
}

export default ActivityFeed;
